// components/BookingProgressTracker.tsx
import React from "react";
import {
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  CreditCardIcon,
  CheckBadgeIcon,
} from "@heroicons/react/24/outline";

interface ProgressStep {
  path: number;
  typeHost: string;
  typeGuest: string;
  status: string;
  icon: React.ReactNode;
}

interface BookingProgressTrackerProps {
  currentStatus: string;
  isHost?: boolean;
}

const BookingProgressTracker: React.FC<BookingProgressTrackerProps> = ({
  currentStatus,
  isHost = false,
}) => {
  const pathArray: ProgressStep[] = [
    {
      path: 1,
      typeHost: "Receive booking request",
      typeGuest: "Send booking request",
      status: "booking_request",
      icon: <ChatBubbleLeftRightIcon className="w-5 h-5" />,
    },
    {
      path: 2,
      typeHost: "Issue invoice",
      typeGuest: "Receive invoice",
      status: "invoice_sent",
      icon: <DocumentTextIcon className="w-5 h-5" />,
    },
    {
      path: 3,
      typeHost: "Receive payment",
      typeGuest: "Make payment",
      status: "payment_received",
      icon: <CreditCardIcon className="w-5 h-5" />,
    },
    {
      path: 4,
      typeHost: "Booking confirmed",
      typeGuest: "Booking confirmed",
      status: "confirmed",
      icon: <CheckBadgeIcon className="w-5 h-5" />,
    },
  ];

  // ✅ FIX: If status is "confirmed", mark all steps as complete
  // Otherwise find the matching step
  const activeStepIndex = 
    currentStatus === "confirmed" 
      ? pathArray.length - 1 
      : pathArray.findIndex((s) => s.status === currentStatus);

  const getStepLabel = (step: ProgressStep) => {
    return isHost ? step.typeHost : step.typeGuest;
  };

  return (
    <div className="relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-xl md:rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6 md:p-8 mb-6 overflow-hidden">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Booking Progress
        </h3>
        <div className="h-1 w-16 sm:w-20 bg-gradient-to-r from-[#F56948] to-orange-400 rounded-full"></div>
      </div>

      {/* Mobile Layout (< md) - Vertical */}
      <div className="md:hidden space-y-4">
        {pathArray.map((step, index) => {
          /*const isActive = index <= activeStepIndex;
          const isCurrentStep = index === activeStepIndex;
          const isCompleted = index < activeStepIndex;*/

          const isActive = currentStatus === "confirmed" ? true : index <= activeStepIndex;
          const isCurrentStep = currentStatus === "confirmed" ? false : index === activeStepIndex;
          const isCompleted = currentStatus === "confirmed" ? true : index < activeStepIndex;

          return (
            <div key={step.path} className="relative">
              <div className="flex items-start gap-4">
                {/* Step Circle with Icon */}
                <div className="flex flex-col items-center flex-shrink-0 relative">
                  <div
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      transition-all duration-500 ease-out relative
                      ${
                        isCompleted
                          ? "bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/30"
                          : isCurrentStep
                          ? "bg-gradient-to-br from-[#F56948] to-[#FF8A6E] text-white shadow-xl shadow-[#F56948]/40 scale-110"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
                      }
                    `}
                  >
                    {isCompleted ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      step.icon
                    )}

                    {/* Outer ring for current step */}
                    {isCurrentStep && (
                      <div className="absolute inset-0 rounded-full border-4 border-[#F56948]/30 dark:border-[#F56948]/50 animate-ping opacity-75"></div>
                    )}
                  </div>

                  {/* Vertical connecting line */}
                  {index < pathArray.length - 1 && (
                    <div className="w-0.5 flex-1 min-h-[40px] mt-2">
                      <div
                        className={`
                          w-full h-full rounded-full transition-all duration-500
                          ${
                            index < activeStepIndex
                              ? "bg-gradient-to-b from-green-500 to-green-600"
                              : index === activeStepIndex
                              ? "bg-gradient-to-b from-[#F56948] to-gray-300 dark:to-gray-700"
                              : "bg-gray-300 dark:bg-gray-700"
                          }
                        `}
                      ></div>
                    </div>
                  )}
                </div>

                {/* Step Content */}
                <div className="flex-1 pt-2">
                  <div
                    className={`
                      text-xs font-bold px-2 py-1 rounded-full mb-2 inline-block
                      ${
                        isActive
                          ? "bg-[#F56948]/10 dark:bg-[#F56948]/20 text-[#F56948] dark:text-[#FF8A6E]"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      }
                    `}
                  >
                    Step {step.path}
                  </div>
                  <p
                    className={`
                      text-sm leading-tight
                      transition-all duration-300
                      ${
                        isActive
                          ? "text-gray-900 dark:text-white font-semibold"
                          : "text-gray-500 dark:text-gray-400 font-medium"
                      }
                    `}
                  >
                    {getStepLabel(step)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop Layout (>= md) - Horizontal */}
      <div className="hidden md:block">
        <div className="relative flex justify-between items-start">
          {pathArray.map((step, index) => {
          const isActive = currentStatus === "confirmed" ? true : index <= activeStepIndex;
          const isCurrentStep = currentStatus === "confirmed" ? false : index === activeStepIndex;
          const isCompleted = currentStatus === "confirmed" ? true : index < activeStepIndex;

            return (
              <div
                key={step.path}
                className="flex flex-col items-center flex-1 relative"
              >
                {/* Step Circle with Icon */}
                <div className="flex flex-col items-center mb-4">
                  <div
                    className={`
                      w-14 h-14 rounded-full flex items-center justify-center mb-3
                      transition-all duration-500 ease-out relative
                      ${
                        isCompleted
                          ? "bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/50 scale-100"
                          : isCurrentStep
                          ? "bg-gradient-to-br from-[#F56948] to-[#FF8A6E] text-white shadow-xl shadow-[#F56948]/50 scale-110 animate-pulse"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
                      }
                    `}
                  >
                    {isCompleted ? (
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      step.icon
                    )}

                    {/* Outer ring for current step */}
                    {isCurrentStep && (
                      <div className="absolute inset-0 rounded-full border-4 border-[#F56948]/30 dark:border-[#F56948]/50 animate-ping opacity-75"></div>
                    )}
                  </div>

                  {/* Step Number Badge */}
                  <div
                    className={`
                      text-xs font-bold px-2 py-1 rounded-full mb-2
                      ${
                        isActive
                          ? "bg-[#F56948]/10 dark:bg-[#F56948]/20 text-[#F56948] dark:text-[#FF8A6E]"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      }
                    `}
                  >
                    Step {step.path}
                  </div>
                </div>

                {/* Step Label */}
                <div className="w-full px-2">
                  <p
                    className={`
                      text-sm text-center leading-tight
                      transition-all duration-300
                      ${
                        isActive
                          ? "text-gray-900 dark:text-white font-semibold"
                          : "text-gray-500 dark:text-gray-400 font-medium"
                      }
                    `}
                  >
                    {getStepLabel(step)}
                  </p>
                </div>

                {/* Horizontal connecting line */}
                {index < pathArray.length - 1 && (
                  <div className="absolute top-7 left-1/2 w-full h-0.5 -z-10">
                    <div
                      className={`
                        h-full rounded-full transition-all duration-500
                        ${
                          index < activeStepIndex
                            ? "bg-gradient-to-r from-green-500 to-green-600"
                            : index === activeStepIndex
                            ? "bg-gradient-to-r from-[#F56948] via-orange-400 to-gray-300 dark:to-gray-700"
                            : "bg-gray-300 dark:bg-gray-700"
                        }
                      `}
                    ></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Status Message */}
      <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {activeStepIndex === pathArray.length - 1 ? (
            <>
              <CheckBadgeIcon className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <p className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400 text-center">
                Booking completed successfully!
              </p>
            </>
          ) : activeStepIndex >= 0 ? (
            <>
              <div className="w-2 h-2 bg-[#F56948] rounded-full animate-pulse flex-shrink-0"></div>
              <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                Current step:{" "}
                <span className="text-[#F56948] dark:text-[#FF8A6E]">
                  {getStepLabel(pathArray[activeStepIndex])}
                </span>
              </p>
            </>
          ) : (
            <>
              <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 text-center">
                Awaiting booking request...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingProgressTracker;